import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Check, X, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { useTaal } from "@/contexts/TaalContext";

/** Een skill, geleerd uit een geslaagde taak. Post b van de batch, BLOK 7b.
 *
 *  NovaMund stelt voor; Frank beslist. De naam en de instructies staan open om
 *  te herschrijven, en wat hij aanneemt is wat hier op glas staat. De skill
 *  krijgt geen gereedschap; dat blijft een aparte keuze op het Skills-scherm.
 */
export default function SkillVoorstel({ voorstel, onBesloten }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(voorstel);
  const [naam, setNaam] = useState(voorstel.naam);
  const [instructies, setInstructies] = useState(voorstel.instructies);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    `${API}/postbus/skill/${voorstel.id}/toon`, rij.status === "open");

  const beslis = async (actie) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/postbus/skill/${rij.id}/${actie}`,
        { token, naam, instructies });
      setRij(r.data);
      onBesloten?.(r.data);
    } catch (err) {
      const tekst = err?.response?.data?.detail || t("Er ging iets mis; er is niets besloten.");
      setFout(tekst);
      // FIX 4: geen stil nieuw token; alleen "Open opnieuw". Punt 5: de reden blijft bewaard.
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (rij.status !== "open") {
    return (
      <div className="border rounded-lg p-3" data-testid="skill-besloten">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          {rij.status === "aangenomen"
            ? `Skill "${naam}" staat bij je skills${rij.gewijzigd ? ", met jouw aanpassing" : ""}.`
            : t("Niet nodig. Er is geen skill gemaakt.")}
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="skill-voorstel">
      {/* ZL3: een vaste vraag (herhaalde_vragen.py) heeft een eigen zin en de telling erbij. */}
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {rij.soort === "vaste_vraag"
          ? t("Deze vraag stel je vaak. Wil je hem bewaren als skill? Er gebeurt niets tot je kiest.")
          : t("Deze taak lukte en is gecontroleerd. Wil je de aanpak bewaren als skill? Er gebeurt niets tot je kiest.")}
      </p>
      <p className="text-xs"><span className="text-[hsl(var(--muted-foreground))]">{rij.soort === "vaste_vraag" ? t("Vraag:") : t("Taak:")} </span>{rij.taak}</p>
      {rij.soort === "vaste_vraag" && (
        <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="skill-telling">
          {t("Gesteld in {g} gesprekken op {d} dagen", { g: rij.gesprekken, d: rij.dagen })}
        </p>
      )}
      <label className="block text-xs">
        {t("Naam")}
        <input className="mt-1 w-full min-w-0 border rounded p-2 text-sm bg-background"
               value={naam} onChange={(e) => setNaam(e.target.value)} data-testid="skill-naam" />
      </label>
      <label className="block text-xs">
        {t("Instructies")}
        <textarea className="mt-1 w-full min-w-0 border rounded p-2 text-xs min-h-28 bg-background"
                  value={instructies} onChange={(e) => setInstructies(e.target.value)}
                  data-testid="skill-instructies" />
      </label>
      {(fout || reden) && <p className="text-xs text-[hsl(var(--destructive))]" data-testid="skill-fout">{fout || reden}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className={`min-h-11 px-4 ${vervallen ? GRIJS : ""}`} disabled={bezig || !token}
                onClick={() => beslis("aannemen")} data-testid="skill-aannemen">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
          {t("Maak skill")}
        </Button>
        <Button size="sm" variant="outline" className="min-h-11 px-4" disabled={bezig || !token}
                onClick={() => beslis("weigeren")} data-testid="skill-weigeren">
          <X className="h-3 w-3 mr-1" /> {t("Niet nodig")}
        </Button>
        {vervallen && <OpenOpnieuw onClick={vernieuw} bezig={bezig} />}
      </div>
    </div>
  );
}
